/* eslint-disable react/no-unescaped-entities */
import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { useCityBuilder } from '../../hooks/useCityBuilder';
import Modal from '../Modal';
import ModalContent from '../ModalContent';
import Tag from '../Tag';
import BlockAmount from './BlockAmount';
import BlockSearch from './BlockSearch';

function BlockSelector({ cityBlockIndex, weight, activeBlock }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [rarityFilter, setRarityFilter] = useState('');
  const { uiBlocks, blocksInCity, setBlocksInCity, originalBlocksInCity } = useCityBuilder();

  const isUpdatedBlock = useMemo(() => {
    const originalBlock = originalBlocksInCity.find((block) => block.index === cityBlockIndex);
    const updatedBlock = blocksInCity.find((block) => block.index === cityBlockIndex);
    if (!originalBlock || !updatedBlock) return false;
    return originalBlock.block?.index !== updatedBlock.block?.index;
  }, [blocksInCity, originalBlocksInCity, cityBlockIndex]);

  const filteredUiBlocks = useMemo(() => {
    return uiBlocks.filter((block) => {
      if (rarityFilter !== '' && block.metadata.properties.Rarity !== rarityFilter) return false;
      if (query === '') return true;
      return block.params.name.toLowerCase().includes(query.toLowerCase());
    });
  }, [query, rarityFilter, uiBlocks]);

  const onSelect = (block) => {
    const updatedBlocks = [...blocksInCity];
    const index = updatedBlocks.findIndex((block) => block.index === cityBlockIndex);

    if (index === -1) {
      updatedBlocks.push({ index: cityBlockIndex, block, weight });
    } else {
      updatedBlocks[index] = { ...updatedBlocks[index], block, weight };
    }

    setBlocksInCity(updatedBlocks);
    setOpen(false);
  };

  useEffect(() => {
    if (!activeBlock) return;
    onSelect(activeBlock);
  }, [activeBlock, cityBlockIndex]);

  const imageExtension = useMemo(() => {
    if (activeBlock?.index === 1213224198) {
      return 'gif';
    } else {
      return 'png';
    }
  }, [activeBlock]);

  return (
    <div className='flex flex-col w-full'>
      {/* <h3 className='text-xl font-bold mb-2'>Block #{cityBlockIndex}</h3> */}

      <button
        type='button'
        className={clsx(`w-full font-bold bg-neutral-800 aspect-square rounded-md`, isUpdatedBlock && 'ring-4 ring-primary-500')}
        onClick={() => setOpen(true)}
      >
        {activeBlock ? (
          <div className={clsx('relative rounded-md')}>
            <div className='absolute z-20 left-0 bottom-0 p-2'>
              <Tag rarity={activeBlock?.metadata?.properties.Rarity} />
            </div>
            <img src={`/blocks/minified/${activeBlock?.index}.${imageExtension}`} alt={activeBlock?.params?.name} className='rounded-md' />
          </div>
        ) : (
          <div className='bg-gray-500 aspect-square rounded-md'></div>
        )}
        <div className='bg-gray-900 md:block w-full rounded-md p-4 mt-2 hidden'>
          {activeBlock ? (
            <div>
              <p className='text-white text-sm text-left flex flex-col gap-0.5'>
                <span className='text-gray-400'>City Block #{cityBlockIndex}</span> <span>{activeBlock?.params.name.split(' - ')?.[1]}</span>
              </p>
            </div>
          ) : (
            <span>Update City Block #{cityBlockIndex}</span>
          )}
        </div>
      </button>

      <Modal open={open} onClose={setOpen} type='large'>
        <ModalContent
          heading={`Update Block #${cityBlockIndex}`}
          description={`Choose a Shitty City Block that you'd like to add to your Shitty City.`}
        >
          <div className='my-10'>
            <BlockSearch onQuery={setQuery} onRarityFilter={setRarityFilter} />
          </div>

          <div className='grid grid-cols-2 md:grid-cols-4 gap-6'>
            {[null, ...filteredUiBlocks]?.map((b, index) => {
              let imageExtension = 'png';
              if (b?.index === 1213224198) {
                imageExtension = 'gif';
              }

              return (
                <div key={b?.index || 'empty'}>
                  <button
                    type='button'
                    className={clsx(
                      'lg:hover:bg-gray-800 relative transition duration-300 flex flex-col w-full h-full rounded-lg min-h-[250px] lg:min-h-[300px]',
                      {
                        'bg-gray-800 flex items-center justify-center': !b,
                        'ring-4 ring-primary-500': b && activeBlock?.index === b.index,
                      }
                    )}
                    onClick={() => onSelect(b)}
                  >
                    {b && (
                      <div className='relative aspect-square w-full'>
                        <div className='absolute z-20 left-0 bottom-0 p-2'>
                          <Tag rarity={b?.metadata?.properties.Rarity} />
                        </div>
                        <img
                          src={`/blocks/minified/${b.index}.${imageExtension}`}
                          alt={b.params?.name}
                          className='absolute inset-0 object-cover rounded-t-lg'
                        />
                      </div>
                    )}

                    {b && (
                      <div className='py-6 lg:p-6 text-left flex justify-between'>
                        <div>
                          <p className='font-medium lg:text-lg'>{b.params.name}</p>
                          <p className='text-gray-500 text-base mt-1'>{b.index}</p>
                        </div>
                        <div className='absolute top-2 right-2 bg-gray-900 rounded-md text-white gap-1  w-10 h-[28px] flex items-end justify-center'>
                          <p className='text-gray-400 text-base mb-0.5'>x</p>
                          <p className='text-right text-xl font-bold mt-1'>{<BlockAmount block={b} />}</p>
                        </div>
                      </div>
                    )}

                    {!b && (
                      <div className='flex flex-col justify-center items-center gap-5 text-gray-600'>
                        <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 512' className='w-[60px]'>
                          <path
                            d='M320.7 351.7C329 262.1 404.3 192 496 192c8.9 0 17.6 .7 26.1 1.9L309.5 7c-6-5-14-7-21-7s-15 1-22 8L10 231.5c-7 7-10 15-10 24c0 18 14 32.1 32 32.1h32V480c0 17.7 14.3 32 32 32H192c17.7 0 32-14.3 32-32V383.7c0-17.7 14.3-32 32-32h64l.7 0zM496 512c79.5 0 144-64.5 144-144s-64.5-144-144-144s-144 64.5-144 144s64.5 144 144 144zm59.3-180.7L518.6 368l36.7 36.7c6.2 6.2 6.2 16.4 0 22.6s-16.4 6.2-22.6 0L496 390.6l-36.7 36.7c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6L473.4 368l-36.7-36.7c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L496 345.4l36.7-36.7c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6z'
                            fill='currentColor'
                          />
                        </svg>
                        <p className='md:text-lg text-center'>Empty City Block</p>
                      </div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </ModalContent>
      </Modal>
    </div>
  );
}

export default BlockSelector;
